import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';

import { LANGUAGES, Language } from '@core/constants/language.constants';
import { CompanySettingService } from '@services/company-setting.service';
import { CompanySetting } from '@models/company-setting.model';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private supportedLangs: Language[] = Object.values(LANGUAGES).filter(
    (lang): lang is Language => lang !== LANGUAGES.ZZ,
  );

  private readonly _currentLanguage = signal<Language>(LANGUAGES.EN);
  readonly currentLanguage = this._currentLanguage.asReadonly();

  private readonly companySettingService = inject(CompanySettingService);

  // The current company's active content languages, tracked reactively from the company stream.
  private readonly _companyActiveLanguages = toSignal(
    this.companySettingService.companySettingListener.pipe(
      map(
        (company: CompanySetting | null) => company?.enabledLanguages ?? null,
      ),
    ),
    { initialValue: null },
  );

  // The company's active languages on guest pages (login/registration/self-registration), where
  // there is no logged-in company stream. Fed from the guest-facing brand settings.
  private readonly _guestActiveLanguages = signal<string[] | null>(null);
  private readonly _guestDefaultLanguage = signal<string | null>(null);

  // THE single frontend read seam for "which languages this company may use". Every language
  // selector reads this. Prefers the logged-in company stream, then the guest set, then falls back
  // to all platform-supported languages (no company context / empty set) so nothing renders empty.
  readonly activeLanguages = computed<Language[]>(() => {
    const enabled =
      this._companyActiveLanguages() ?? this._guestActiveLanguages();
    if (!enabled?.length) {
      return this.getSupportedLanguages();
    }
    // zz is a dev tool, not a company content language — never filter it out.
    const active = this.supportedLangs.filter(
      lang =>
        enabled.includes(lang) ||
        (environment.development && lang === LANGUAGES.ZZ),
    );
    return active.length ? active : this.getSupportedLanguages();
  });

  constructor(
    private translateService: TranslateService,
    private appTitle: Title,
  ) {
    if (environment.development) {
      this.supportedLangs.push(LANGUAGES.ZZ);
    }
  }

  // Guest-page company context (login/registration/self-registration): the company's active
  // languages and default, so the selector can scope and the clamp below can fall back correctly.
  setGuestLanguageContext(
    enabledLanguages: string[] | null | undefined,
    defaultLanguage: string | null | undefined,
  ): void {
    this._guestActiveLanguages.set(
      enabledLanguages?.length ? enabledLanguages : null,
    );
    this._guestDefaultLanguage.set(defaultLanguage ?? null);
  }

  getGuestDefaultLanguage(): string | null {
    return this._guestDefaultLanguage();
  }

  setMemberLanguage(memberLang: string, companyDefaultLang: string): void {
    const active = this.activeLanguages();

    if (this.isSupported(memberLang) && active.includes(memberLang)) {
      this._currentLanguage.set(memberLang);
    } else if (companyDefaultLang && this.isSupported(companyDefaultLang)) {
      // The requested language is not available for this company — fall straight to the company
      // default (no intermediate browser-language guess).
      this._currentLanguage.set(companyDefaultLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.isSupported(browserLang)) {
        this._currentLanguage.set(browserLang);
      }
    }

    this.setLanguage();
  }

  setLanguage(): void {
    this.translateService.addLangs(this.supportedLangs);
    this.translateService.setDefaultLang(
      environment.development ? LANGUAGES.ZZ : LANGUAGES.EN,
    );

    this.translateService
      .use(this._currentLanguage())
      .pipe(take(1))
      .subscribe(() =>
        this.appTitle.setTitle(this.translateService.instant('app.title')),
      );

    window.WEBSPELLCHECKER_CONFIG.localization = this._currentLanguage();
  }

  setLanguageFromCookieOrBrowser(cookieLang: string): void {
    if (this.isSupported(cookieLang)) {
      this._currentLanguage.set(cookieLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.isSupported(browserLang)) {
        this._currentLanguage.set(browserLang);
      }
    }
    this.setLanguage();
  }

  getSupportedLanguages(): Language[] {
    return this.supportedLangs;
  }

  // Company's active set plus the record's own (possibly deactivated) language: deactivation is a
  // visibility filter, never data loss, so an already-authored value still renders.
  activeLanguagesIncluding(code: string | undefined | null): Language[] {
    const active = this.activeLanguages();
    if (code && this.isSupported(code) && !active.includes(code)) {
      return [...active, code];
    }
    return active;
  }

  /**
   * The training's own source plus ADDED translation languages — NOT the company-wide active set.
   * `including` keeps an existing record's language selectable after removal from the training.
   */
  trainingLanguagesIncluding(
    sourceLanguage: string | undefined | null,
    translationLanguages: string[] | undefined | null,
    including?: string | null,
  ): Language[] {
    const codes = [sourceLanguage, ...(translationLanguages ?? []), including];
    const result: Language[] = [];
    codes.forEach(code => {
      if (code && this.isSupported(code) && !result.includes(code)) {
        result.push(code);
      }
    });
    return result;
  }

  getDefaultLanguage(): Language {
    return LANGUAGES.EN;
  }

  private isSupported(lang: string): lang is Language {
    return (this.supportedLangs as string[]).includes(lang);
  }
}
